import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listReports } from "@/lib/api/admin";
import type { Report } from "@/lib/api/types";
import { DataTable, type Column } from "@/components/DataTable";
import { Pagination } from "@/components/Pagination";
import { StatusBadge } from "@/components/StatusBadge";
import { PAGE_SIZE, REPORT_STATUSES, REPORT_TARGET_TYPES } from "@/lib/constants";
import { formatDate } from "@/lib/utils/date";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

export function ReportsPage() {
  const user = useAdminAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState("pending");
  const [targetType, setTargetType] = useState("all");
  const [offset, setOffset] = useState(0);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["reports", status, targetType, offset],
    queryFn: async () =>
      listReports(user!, {
        status: status === "all" ? undefined : status,
        targetType: targetType === "all" ? undefined : targetType,
        limit: PAGE_SIZE,
        offset,
      }),
    enabled: !!user,
  });

  const columns: Column<Report>[] = [
    {
      key: "target",
      header: "Target",
      render: (r) => (
        <div>
          <span className="text-xs text-muted-foreground uppercase">{r.target_type}</span>
          <p className="text-sm font-mono text-xs">{r.target_id}</p>
        </div>
      ),
    },
    { key: "reason", header: "Reason", render: (r) => <span className="text-sm">{r.reason}</span> },
    {
      key: "reporter",
      header: "Reporter",
      render: (r) => (
        <span className="text-sm">
          {r.reporter_first_name} {r.reporter_last_name}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => <StatusBadge status={r.status} />,
    },
    {
      key: "date",
      header: "Submitted",
      render: (r) => (
        <span className="text-sm text-muted-foreground">{formatDate(r.created_at)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reports</h1>
          <p className="text-sm text-muted-foreground">User-submitted content reports</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          <RefreshCw className="size-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v);
            setOffset(0);
          }}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {REPORT_STATUSES.map((s) => (
              <SelectItem key={s} value={s} className="capitalize">
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={targetType}
          onValueChange={(v) => {
            setTargetType(v);
            setOffset(0);
          }}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {REPORT_TARGET_TYPES.map((t) => (
              <SelectItem key={t} value={t} className="capitalize">
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading}
        rowKey={(r) => r.id}
        emptyMessage="No reports found."
        onRowClick={(r) => void navigate(`/reports/${r.id}`)}
      />

      {data && (
        <Pagination
          offset={offset}
          limit={PAGE_SIZE}
          total={data.total}
          onOffsetChange={setOffset}
        />
      )}
    </div>
  );
}
